import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModuleFilter } from "../../utils/ModuleFilter";
import { trainingApi } from "../../../api/training";
import { normalizeError } from "../../../operationFeedback/normalizeError";
import {
  buttonClass,
  Feedback,
  formatDate,
} from "./trainingAdminShared";

const stateLabels = {
  pending: "trainingAdmin.deliveryPending",
  sending: "trainingAdmin.deliverySending",
  sent: "trainingAdmin.deliverySent",
  failed: "trainingAdmin.deliveryFailed",
  unknown: "trainingAdmin.deliveryUnknown",
  cancelled: "trainingAdmin.deliveryCancelled",
};
const eventLabels = {
  completed: "trainingAdmin.completed",
  overdue: "trainingAdmin.overdue",
  blocked: "trainingAdmin.blocked",
  digest: "trainingAdmin.deliveryDigest",
};

export default function TrainingAdminDeliveries() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState({ statuses: [] });
  const [guides, setGuides] = useState([]);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const paramsKey = JSON.stringify({
    guide_id: filters.guides?.length ? filters.guides : undefined,
    status: filters.statuses?.join(","),
    offset,
    limit: 50,
  });
  useEffect(() => {
    let current = true;
    trainingApi
      .reportGuides({ limit: 100 })
      .then((result) => {
        if (current) setGuides(result.data || []);
      })
      .catch((failure) => {
        if (current) setError(normalizeError(failure));
      });
    return () => {
      current = false;
    };
  }, []);
  useEffect(() => {
    let current = true;
    setLoading(true);
    trainingApi
      .deliveries(JSON.parse(paramsKey))
      .then((result) => {
        if (current) {
          setRows(result.data || []);
          setCount(result.count || 0);
          setError(null);
        }
      })
      .catch((failure) => {
        if (current) {
          setRows([]);
          setError(normalizeError(failure));
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [paramsKey, refresh]);
  return (
    <div className="space-y-5">
      <p className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        {t("trainingAdmin.deliveryPolicy")}
      </p>
      <ModuleFilter
        filterCard
        hideSearch
        initialFilters={filters}
        customDropdownFilters={[{
          key: "guides",
          label: "trainingAdmin.guide",
          options: guides.map((guide) => ({ value: guide.id, label: guide.title })),
        }]}
        statusOptions={Object.entries(stateLabels).map(([value, label]) => ({
          value,
          label: t(label),
        }))}
        onFiltersChange={(next) => {
          setFilters(next);
          setOffset(0);
        }}
      />
      <div className="flex justify-end">
        <button
          className={buttonClass}
          disabled={loading}
          onClick={() => setRefresh((value) => value + 1)}
        >
          {t("trainingAdmin.reload")}
        </button>
      </div>
      <Feedback error={error} />
      <div
        className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
        aria-busy={loading}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                {[
                  "trainingAdmin.guide",
                  "trainingAdmin.recipient",
                  "trainingAdmin.event",
                  "trainingAdmin.status",
                  "trainingAdmin.scheduledAt",
                  "trainingAdmin.sentAt",
                ].map((key) => (
                  <th key={key} className="p-4">
                    {t(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-slate-100 align-top"
                >
                  <td className="p-4 font-semibold">{row.guide?.title}</td>
                  <td className="p-4">{row.recipient?.title}</td>
                  <td className="p-4">
                    {t(
                      eventLabels[row.event] || "trainingAdmin.deliveryDigest",
                    )}
                  </td>
                  <td className="p-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${row.state === "sent" ? "bg-emerald-50 text-emerald-800" : ["failed", "unknown"].includes(row.state) ? "bg-amber-50 text-amber-900" : "bg-slate-100 text-slate-700"}`}
                    >
                      {t(
                        stateLabels[row.state] ||
                          "trainingAdmin.deliveryUnknown",
                      )}
                    </span>
                    {row.state === "unknown" && (
                      <p className="mt-2 max-w-xs text-xs text-amber-900">
                        {t("trainingAdmin.deliveryNoRetry")}
                      </p>
                    )}
                    {row.last_error && (
                      <p className="mt-2 max-w-xs text-xs text-slate-500">
                        {t("trainingAdmin.deliveryFailureHint")}
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap p-4">
                    {formatDate(
                      row.scheduled_at || row.created_at,
                      i18n.language,
                    )}
                  </td>
                  <td className="whitespace-nowrap p-4">
                    {formatDate(row.sent_at, i18n.language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && !rows.length && (
            <p className="p-10 text-center text-sm text-slate-500">
              {t("trainingAdmin.noResults")}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 p-3">
          <span className="text-xs tabular-nums text-slate-500">
            {count ? offset + 1 : 0}–{Math.min(offset + rows.length, count)} /{" "}
            {count}
          </span>
          <div className="flex gap-2">
            <button
              className={buttonClass}
              disabled={loading || offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 50))}
            >
              {t("trainingAdmin.previous")}
            </button>
            <button
              className={buttonClass}
              disabled={loading || offset + 50 >= count}
              onClick={() => setOffset(offset + 50)}
            >
              {t("trainingAdmin.next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
